import Container, { type ContainerProps } from "@mui/joy/Container"
import { type PropsWithChildren, useEffect } from "react"
import { useStore } from "zustand"
import { useAccount } from "@levana/chain/account"
import { ModalPresenter } from "@levana/utils/modal"

import { Event } from "@perps/analytics/events"
import { EventKey } from "@perps/analytics/constants"
import { track } from "@perps/analytics/track"
import FooterLayout from "@perps/footer/FooterLayout"
import BannerProvider from "@perps/banner/BannerContext"
import SystemHealth from "@future/systemHealth"
import AnnouncementsBanner from "@future/banner/AnnouncementsBanner"
import { updateTargetDataInReports } from "@future/target/state"
import UpdatesBanner from "@future/banner/UpdatesBanner"
import ReportEnabledDecider from "@future/libs/error/ReportEnabledDecider"
import SeiSuspendBanner from "@future/banner/SeiSuspendBanner"
import type { ContextStoreProp } from "@future/context/store"
import { useClosedPositionsStore } from "@future/pages/tradeMarket/openPositions/useClosedPositionsStore"
import ReferralModalPresenter from "@future/referral/ReferralModalPresenter"
import ConnectModal from "@future/connect/ConnectModal"
import { useClosedLimitOrdersStore } from "@future/pages/tradeMarket/limitOrders/useClosedLimitOrdersStore"
import Header from "@future/header/Header"
import { RouteRootId } from "@future/router/types"

import OnlineWatcher from "../OnlineWatcher"

// TODO: merge this with the new RootLayout
const RootLayout_Inner = (
  props: PropsWithChildren<ContextStoreProp<"standard">>,
) => {
  const routeRootId = useStore(
    props.contextStore,
    (state) => state.route.rootId,
  )

  const includeAccount = routeRootId !== "stats"

  usePageViewedAnalytics(props)
  useClearClosedPositions(props)
  useSyncReportMetadata(props)

  return (
    <BannerProvider>
      <ReportEnabledDecider />
      <SystemHealth contextStore={props.contextStore} />
      <OnlineWatcher />
      <AnnouncementsBanner />
      <UpdatesBanner />
      <SeiSuspendBanner contextStore={props.contextStore} />

      <ModalPresenter queueKey={ConnectModal.name}>
        <ConnectModal contextStore={props.contextStore} />
      </ModalPresenter>

      <ReferralModalPresenter contextStore={props.contextStore} />

      <Header
        contextStore={props.contextStore}
        includeAccount={includeAccount}
      />

      <Container
        // disableGutters // ???: should this be done for mobile
        maxWidth={getMaxBreakpoint(routeRootId)}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          flexWrap: "nowrap",
          justifyContent: "space-between",
        }}
      >
        {props.children}
        <FooterLayout />
      </Container>
    </BannerProvider>
  )
}

const getMaxBreakpoint = (
  rootId: RouteRootId | undefined,
): NonNullable<ContainerProps["maxWidth"]> => {
  switch (rootId) {
    case RouteRootId.markets:
      return "xl"
    case RouteRootId.earn:
      return "xl"
    case RouteRootId.history:
      return "xl"
    case RouteRootId.leaderboard:
      return "xl"
    case RouteRootId.stats:
      return "xl"
    case RouteRootId.trade:
      return false
    case undefined:
      return "xl"
  }
}

const usePageViewedAnalytics = (props: ContextStoreProp<"standard">) => {
  const { data: account } = useAccount()
  const targetId = useStore(props.contextStore, (state) => state.targetInfo.id)

  // biome-ignore lint/correctness/useExhaustiveDependencies: rerender for `targetId` and `account`
  useEffect(() => {
    track(
      Event.pageViewed({
        [EventKey.URL_PATH]: `${location.pathname}${location.search}`,
      }),
    )
  }, [targetId, account])
}

const useClearClosedPositions = (props: ContextStoreProp<"standard">) => {
  const targetId = useStore(props.contextStore, (state) => state.targetInfo.id)
  const { data: account } = useAccount()

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear for `targetId`
  useEffect(() => {
    if (!account) {
      useClosedPositionsStore.getState().clear()
      useClosedLimitOrdersStore.getState().clear()
    }
  }, [targetId, account])
}

const useSyncReportMetadata = (props: ContextStoreProp<"standard">) => {
  const targetId = useStore(props.contextStore, (state) => state.targetInfo.id)
  const factoryAddress = useStore(
    props.contextStore,
    (state) => state.chain.config.factoryAddress,
  )

  useEffect(() => {
    updateTargetDataInReports(targetId, factoryAddress)
  }, [targetId, factoryAddress])
}

export default RootLayout_Inner
